<template>
  <div class="chat-message-entry-root is-flex" @mouseover="$el.classList.add('hovered')" @mouseout="$el.classList.remove('hovered')">
    <VueShowdown v-show="!editing" class="text-content"
        :options="markdownAttributes"
        :markdown="safeMessage(msg, edited)"
        tag="span"
        :extensions="extensions"/>
    <div v-show="editing">
      <textarea class="edit-msg" :value="rawText" ref="edit" @keydown.enter.exact.stop.prevent="editMessage" @keydown.ctrl.enter.exact.stop.prevent="editMessage" @keydown.esc.exact="$emit('update:editing', false)"></textarea>
    </div>
    <a v-show="enableDropdown" class="options dropdown-trigger" :data-target="dropdownID">
      <i-mdi-chevron-down width="16" height="16" title=""/>
    </a>
    <div class="dropdown-content" :id="dropdownID">
      <!-- allowPin is about privileges, showPin is about logic of when and where it should be shown -->
      <div v-show="showPin" class="dropdown-item-container" :class="{disabled: !allowPin}">
        <Crown class="locked-privilege-crown"/>
        <a class="dropdown-item" @click="allowPin && pinMessage(true)" :title="getDropdownTitle(allowPin)">
          Pin
        </a>
      </div>
      <div v-show="showUnpin" class="dropdown-item-container" :class="{disabled: !allowUnpin}">
        <Crown class="locked-privilege-crown"/>
        <a class="dropdown-item" @click="allowUnpin && pinMessage(false)" :title="getDropdownTitle(allowUnpin)">
          Unpin
        </a>
      </div>
      <div v-show="showEdit" class="dropdown-item-container" :class="{disabled: !allowEdit}">
        <Crown class="locked-privilege-crown"/>
        <a class="dropdown-item" @click="triggerEdit" :title="getDropdownTitle(allowEdit)">
          Edit
        </a>
      </div>
      <div v-show="showDelete" class="dropdown-item-container" :class="{disabled: !allowDelete}">
        <Crown class="locked-privilege-crown"/>
        <a class="dropdown-item" @click="allowDelete && deleteMessage()" :title="getDropdownTitle(allowDelete)">
          Delete
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { privileges as Privileges } from '@root/backend/patron-privileges/privileges'

const options = {
  emoji: true,
  simplifiedAutoLink: true,
  simpleLineBreaks: true,
  openLinksInNewWindow: true,
  strikethrough: true
}

export default {
  name: 'chat-message-entry',
  props: ['uuid', 'msg', 'safeMessage', 'extensions', 'allowPin', 'allowUnpin', 'allowEdit', 'allowDelete', 'showPin', 'showUnpin', 'showEdit', 'showDelete', 'editing', 'edited'],
  components: {
    VueShowdown: () => import(/* webpackChunkName: "vue-showdown" */ 'vue-showdown/src/components/vue-showdown'),
    Crown: () => import(/* webpackChunkName: "crown-2" */ '@C/crown.vue')
  },
  computed: {
    ...mapGetters([
      'Strings'
    ]),
    enableDropdown () {
      return this.showPin || this.showEdit || this.showDelete || this.showUnpin
    },
    markdownAttributes () {
      return options
    },
    dropdownID () {
      return `${this.uuid}` + 'dropdown'
    },
    hasPinPrivilege () {
      const { roomProperties: { privileges } } = this
      return privileges[Privileges.TEXT_CHAT_PIN_MESSAGES]
    },
    hasEditPrivilege () {
      const { roomProperties: { privileges } } = this
      return privileges[Privileges.TEXT_CHAT_EDIT_MESSAGES]
    },
    hasDeletePrivilege () {
      const { roomProperties: { privileges } } = this
      return privileges[Privileges.TEXT_CHAT_DELETE_MESSAGES]
    },
    rawText () {
      const { msg } = this
      if (typeof msg === 'string') {
        return msg
      }
      return msg.msg
    }
  },
  watch: {
    async editing (v) {
      if (v) {
        const { $refs: { edit: textarea } } = this
        textarea.setSelectionRange(textarea.value.length, textarea.value.length)
        textarea.focus()
      }
    },
    msg () {
      if (this.editing) {
        // edit complete
        this.$emit('update:editing', false)
      }
    }
  },
  data () {
    return {
    }
  },
  methods: {
    editMessage (e) {
      let { target: { value: msg } } = e
      const { uuid } = this
      msg = msg.trim()
      if (msg.length === 0) {
        return this.deleteMessage()
      } else {
        this.$emit('edit', { uuid, msg })
      }
      this.$emit('update:editing', false)
    },
    pinMessage (pin) {
      const { uuid } = this
      return this.$emit('pin', { uuid, pin })
    },
    deleteMessage () {
      const { uuid } = this
      return this.$emit('delete', { uuid })
    },
    getDropdownTitle (isAllowed) {
      return isAllowed ? '' : this.Strings.PRIVILEGED_FEATURE
    },
    triggerEdit () {
      if (this.allowEdit) {
        this.$emit('update:editing', true)
      }
    }
  },
  beforeDestroy () {
    this.dropdown && this.dropdown.destroy()
  },
  mounted () {
    this.dropdown = M.Dropdown.init(this.$el.querySelector('.dropdown-trigger'), {
      alignment: 'right',
      container: document.body,
      coverTrigger: true
    })
  }
}
</script>

<style lang="scss" scoped>
::v-deep h1, h2, h3, h4, h5, h6 {
  font-weight: 400;
  line-height: 110%;
}

::v-deep h1 {
  font-size: 4.2rem;
  margin: 2.8rem 0 1.68rem 0;
}
::v-deep h2 {
  font-size: 3.56rem;
  margin: 2.373rem 0 1.424rem 0;
}
::v-deep h3 {
  font-size: 2.92rem;
  margin: 1.9467rem 0 1.168rem 0;
}
::v-deep h4 {
  font-size: 2.28rem;
  margin: 1.52rem 0 0.912rem 0;
}
::v-deep h5 {
  font-size: 1.64rem;
  margin: 1.093rem 0 0.656rem 0;
}
::v-deep h6 {
  font-size: 1.15rem;
  margin: 0.767rem 0 0.46rem 0;
}

::v-deep li {
  list-style-position: inside;
  display: list-item;
}

::v-deep ul {
  li {
    list-style-type: disc;
  }
}

::v-deep blockquote {
  > p {
    margin-left: 0.5em;
  }
}

::v-deep strong {
  color: inherit;
}

.chat-message-entry-root {
  flex-direction: row;
  &.hovered {
    .options {
      opacity: 1;
      visibility: visible;
    }
  }

  .text-content {
    flex: 1;
  }
  .options {
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s linear;
  }
  textarea {
    color: inherit;
    resize: none;
  }
}

.dropdown-content {
  .dropdown-item-container {
    position: relative;
    &:not(.disabled):hover {
      background-color: whitesmoke;
    }
    &.disabled {
      filter: grayscale(90%);
    }
    a.dropdown-item {
      &:hover {
        background-color: transparent;
        color: #4a4a4a;
      }
    }
  }
}
</style>
